/**
 * UserBadges — inntil fire firkantede status-merker ved siden av brukernavn.
 *
 * Regler:
 *   - VIP (V) vises hvis brukeren er VIP
 *   - CREATOR (★) vises hvis brukeren er godkjent skaper
 *   - SELGER (S) vises hvis brukeren er godkjent selger
 *   - VERIFISERT (✓) vises hvis brukeren er BankID-verifisert
 *   - Alle 4 vises samtidig hvis brukeren har alle statusene
 *
 * Hover viser tydelig forklaring på hvert merke.
 *
 * Bruk:
 *   <UserBadges user={u} size="sm" />
 *   <UserBadges user={u} size="sm" hideCreator />   // på /skapere-siden
 */
import React from "react";

const SIZE_MAP = {
  sm: { box: "h-3.5 w-3.5 text-[10px]" },
  md: { box: "h-4 w-4 text-[11px]" },
  lg: { box: "h-5 w-5 text-[13px]" },
};

function Pill({ label, title, color, symbol, testid }) {
  return (
    <span
      title={title}
      data-testid={testid}
      className={`inline-flex items-center justify-center font-semibold shrink-0 leading-none cursor-help ${label.box} ${color}`}
      style={{ lineHeight: 1, letterSpacing: 0, paddingLeft: 0, paddingRight: 0, textIndent: 0 }}
    >
      {symbol}
    </span>
  );
}

export default function UserBadges({
  user,
  size = "sm",
  hideCreator = false,
  className = "",
}) {
  if (!user) return null;

  const tier = (user.tier || "").toLowerCase();
  const isVip = !!user.is_vip || tier === "vip" || tier === "creator";
  const isCreator = !!user.is_creator && !hideCreator;
  const isSeller = !!user.seller_verified;
  const isVerified =
    !!user.bankid_verified ||
    !!user.is_verified ||
    (user.verification_level || 0) >= 1;

  if (!isVip && !isCreator && !isSeller && !isVerified) return null;

  const label = SIZE_MAP[size] || SIZE_MAP.sm;

  return (
    <span className={`inline-flex items-center gap-0.5 shrink-0 ${className}`}>
      {isVip && (
        <Pill
          label={label}
          title="V — VIP-medlem: betalende medlem med utvidede fordeler"
          color="bg-[var(--gold)] text-[var(--noir)]"
          symbol="V"
          testid="badge-vip"
        />
      )}
      {isCreator && (
        <Pill
          label={label}
          title="★ — Verifisert skaper: godkjent innholdsprodusent på plattformen"
          color="bg-[var(--magenta)] text-black"
          symbol="★"
          testid="badge-creator"
        />
      )}
      {isSeller && (
        <Pill
          label={label}
          title="S — Verifisert selger: godkjent for å selge innhold/tjenester"
          color="bg-emerald-500 text-black"
          symbol="S"
          testid="badge-seller"
        />
      )}
      {isVerified && (
        <Pill
          label={label}
          title="✓ — BankID-verifisert: identiteten er bekreftet med norsk BankID"
          color="bg-sky-500 text-black"
          symbol="✓"
          testid="badge-verified"
        />
      )}
    </span>
  );
}
